import { useState } from 'react';
import { useApi } from '@/api/useApi';
import { useMutationProvider } from '@/hooks/use-mutation-provider';

interface UploadInfo {
	baseUrl: string;
	path: string;
	algorithm: string;
	date: string;
	signedHeaders: string;
	expires: string;
	credential: string;
	signature: string;
	filename: string;
}

interface UploadResponse {
	data: {
		data: UploadInfo;
		message: string;
	};
}

interface UploadState {
	isUploading: boolean;
	progress: number;
	error: string | null;
	success: boolean;
}

interface CreateDocumentRequest {
	documentLink: string;
	documentTypeId: number;
}

interface CreateDocumentResponse {
	data: {
		data: {
			id: number;
			documentLink: string;
			documentTypeId: number;
		};
		message: string;
	};
}

interface UploadResult {
	path: string;
	filename: string;
	filePath: string;
}

interface DocumentTypeConfig {
	VIDEO: 'VIDEO';
	DOCUMENT: 'DOCUMENT';
	IMAGE: 'IMAGE';
}

export const DocumentTypes: DocumentTypeConfig = {
	VIDEO: 'VIDEO',
	DOCUMENT: 'DOCUMENT',
	IMAGE: 'IMAGE'
} as const;

export type DocumentType = keyof typeof DocumentTypes;

export const useDocumentUpload = () => {
	const [uploadState, setUploadState] = useState<UploadState>({
		isUploading: false,
		progress: 0,
		error: null,
		success: false
	});

	const { ApplicantDocumentControllerApi } = useApi();

	const getUploadInfoMutation = useMutationProvider<UploadResponse, Error, DocumentType>(
		async (documentType: DocumentType) => {
			const response = await ApplicantDocumentControllerApi.getUploadInfo(documentType);
			return response as UploadResponse;
		},
		{
			onError: (error: Error) => {
				setUploadState((prev: UploadState) => ({
					...prev,
					error: error?.message || 'Upload failed',
					isUploading: false
				}));
			}
		}
	);

	const createDocumentMutation = useMutationProvider<CreateDocumentResponse, Error, CreateDocumentRequest>(
		async (data: CreateDocumentRequest) => {
			const response = await ApplicantDocumentControllerApi.createApplicantDocument(data);
			return response as CreateDocumentResponse;
		},
		{
			onError: (error: Error) => {
				setUploadState((prev: UploadState) => ({
					...prev,
					error: error?.message || 'Document creation failed',
					isUploading: false
				}));
			}
		}
	);

	const getUploadInfo = async (documentType: DocumentType) => {
		try {
			const response = await getUploadInfoMutation.mutateAsync(documentType);
			if (!response.data?.data) {
				console.log('Failed to get upload URL');
				return null;
			}
			return response.data.data;
		} catch (error) {
			console.log('Error getting upload info:', error);
			return null;
		}
	};

	const uploadToS3 = async (file: File, uploadInfo: UploadInfo) => {
		try {
			const uploadUrl = `${uploadInfo.baseUrl}/${uploadInfo.path}/${uploadInfo.filename}`;
			const uploadUrlWithParams = `${uploadUrl}?X-Amz-Algorithm=${uploadInfo.algorithm}&X-Amz-Credential=${uploadInfo.credential}&X-Amz-Date=${uploadInfo.date}&X-Amz-Expires=${uploadInfo.expires}&X-Amz-SignedHeaders=${uploadInfo.signedHeaders}&X-Amz-Signature=${uploadInfo.signature}`;

			const response = await fetch(uploadUrlWithParams, {
				method: 'PUT',
				body: file
			});

			if (!response.ok) {
				console.log(`Upload failed: ${response.statusText}`);
				return null;
			}

			return {
				path: uploadInfo.path,
				filename: uploadInfo.filename
			};
		} catch (error) {
			console.log('Error uploading to S3:', error);
			return null;
		}
	};

	const getInfoAndUploadToS3 = async (file: File, documentType: DocumentType): Promise<UploadResult | null> => {
		try {
			setUploadState(prev => ({
				...prev,
				isUploading: true,
				progress: 0
			}));

			const uploadInfo = await getUploadInfo(documentType);
			if (!uploadInfo) {
				setUploadState(prev => ({
					...prev,
					error: 'Failed to get upload info',
					isUploading: false
				}));
				return null;
			}

			const uploadResult = await uploadToS3(file, uploadInfo);
			if (!uploadResult) {
				setUploadState(prev => ({
					...prev,
					error: 'Failed to upload to S3',
					isUploading: false
				}));
				return null;
			}

			setUploadState(prev => ({
				...prev,
				isUploading: false,
				progress: 100,
				success: true
			}));

			return {
				...uploadResult,
				filePath: `${uploadInfo.path}/${uploadInfo.filename}`
			};
		} catch (error) {
			console.log('Error in getInfoAndUploadToS3:', error);
			setUploadState(prev => ({
				...prev,
				error: error instanceof Error ? error.message : 'Upload failed',
				isUploading: false
			}));
			return null;
		}
	};

	const createDocument = async (documentLink: string, documentTypeId: number) => {
		try {
			const response = await createDocumentMutation.mutateAsync({
				documentLink,
				documentTypeId
			});
			return response.data.data;
		} catch (error) {
			console.log('Error creating document:', error);
			return null;
		}
	};

	const uploadDocument = async (file: File, documentType: DocumentType, documentTypeId: number) => {
		try {
			setUploadState({
				isUploading: true,
				progress: 0,
				error: null,
				success: false
			});

			const uploadResult = await getInfoAndUploadToS3(file, documentType);
			if (!uploadResult) return null;

			const documentLink = `${uploadResult.path}/${uploadResult.filename}`;
			const createResult = await createDocument(documentLink, documentTypeId);
			if (!createResult) return null;

			setUploadState({
				isUploading: false,
				progress: 100,
				error: null,
				success: true
			});

			return {
				filename: uploadResult.filename,
				documentLink,
				id: createResult.id
			};
		} catch (error) {
			console.log('error', error);
			setUploadState({
				isUploading: false,
				progress: 0,
				error: error instanceof Error ? error.message : 'Upload failed',
				success: false
			});
			return null;
		}
	};

	const resetUploadState = () => {
		setUploadState({
			isUploading: false,
			progress: 0,
			error: null,
			success: false
		});
	};

	return {
		uploadDocument,
		getUploadInfo,
		uploadToS3,
		getInfoAndUploadToS3,
		createDocument,
		resetUploadState,
		uploadState,
		isLoading: getUploadInfoMutation.isLoading || createDocumentMutation.isLoading || uploadState.isUploading
	};
};
